import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ChevronLeft,
  LogOut,
  Copy,
  Check,
  RefreshCw,
  Heart,
  Moon,
  Activity,
  Footprints,
  Upload,
  Zap,
  Camera,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Area,
  AreaChart,
} from "recharts";

// Fields the LIVUP iOS app surfaces on its "Daily Report" screen.
const ENTRY_FIELDS = [
  { key: "hrv_ms",        label: "HRV",          unit: "ms",   step: "1",   placeholder: "e.g. 68" },
  { key: "rhr_bpm",       label: "Resting HR",   unit: "bpm",  step: "1",   placeholder: "e.g. 54" },
  { key: "sleep_minutes", label: "Sleep total",  unit: "min",  step: "1",   placeholder: "e.g. 456" },
  { key: "rem_minutes",   label: "REM",          unit: "min",  step: "1",   placeholder: "e.g. 96" },
  { key: "deep_minutes",  label: "Deep",         unit: "min",  step: "1",   placeholder: "e.g. 78" },
  { key: "spo2_pct",      label: "SpO₂",         unit: "%",    step: "0.1", placeholder: "e.g. 97" },
  { key: "steps",         label: "Steps",        unit: "",     step: "1",   placeholder: "e.g. 5200" },
  { key: "skin_temp_c",   label: "Skin temp",    unit: "°C",   step: "0.1", placeholder: "e.g. 34.6" },
];

function MetricCard({ Icon, label, value, unit, color = "var(--brand-secondary)" }) {
  return (
    <div className="rounded-xl ink-card p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">{label}</div>
        <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}22`, color }}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <div className="font-display font-black text-3xl font-mono-num text-white">{value ?? "—"}</div>
        {value != null && unit && <div className="text-xs text-white/40">{unit}</div>}
      </div>
    </div>
  );
}

export default function PlayerLivup() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [entry, setEntry] = useState({});
  const fileInputRef = useRef(null);

  const handleScan = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!f) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(f.type)) {
      toast.error("Use a PNG, JPEG, or WEBP screenshot");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Screenshot is larger than 8 MB — try a smaller one");
      return;
    }
    setScanBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/livup/me/parse-screenshot", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fields = r.data?.fields || {};
      const count = Object.keys(fields).length;
      if (count === 0) {
        toast.error("Couldn't read any LIVUP fields from that image");
        return;
      }
      // Auto-fill the entry form with parsed values
      setEntry((prev) => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(fields)) {
          next[k] = String(v);
        }
        return next;
      });
      toast.success(`Scanned ${count} field${count === 1 ? "" : "s"} · review and submit`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScanBusy(false);
    }
  };

  const load = async () => {
    const r = await api.get("/livup/me");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  // Auto-trigger the file picker when the page is opened via the Quick-log FAB
  // (/portal/livup?scan=1). We strip the param immediately so a refresh doesn't
  // re-open the picker.
  useEffect(() => {
    if (searchParams.get("scan") === "1") {
      setSearchParams({}, { replace: true });
      // Slight delay so the hidden input is mounted + user gesture is preserved
      // from the route navigation. iOS Safari still allows the click here
      // because the navigation itself was triggered by a user tap.
      setTimeout(() => fileInputRef.current?.click(), 150);
    }
  }, [searchParams, setSearchParams]);

  const copy = async () => {
    if (!data?.webhook_url) return;
    await navigator.clipboard.writeText(data.webhook_url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
    toast.success("Webhook URL copied");
  };

  const rotate = async () => {
    if (!window.confirm("Rotate your LIVUP webhook URL? The old URL stops working immediately.")) return;
    try {
      await api.post("/livup/me/rotate-token");
      await load();
      toast.success("New webhook URL generated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitEntry = async (e) => {
    e?.preventDefault?.();
    const payload = {};
    for (const [k, v] of Object.entries(entry)) {
      if (v === "" || v === null || v === undefined) continue;
      const n = Number(v);
      if (!Number.isFinite(n)) continue;
      payload[k] = n;
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Add at least one metric");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/livup/me/manual-entry", payload);
      toast.success(`Logged ${r.data.days_written} day · readiness will update`);
      setEntry({});
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const handleLogout = async () => { await logout(); nav("/", { replace: true }); };

  const daily = data?.daily || [];
  const sorted = [...daily].sort((a, b) => a.date.localeCompare(b.date));
  const latest = sorted[sorted.length - 1] || {};
  const chart = sorted.slice(-14).map((d) => ({
    date: new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    hrv: d.hrv_avg,
    rhr: d.resting_hr,
    sleep: d.sleep_hours,
  }));

  return (
    <div className="min-h-screen pb-16" style={{ backgroundColor: "var(--ink-1)" }} data-testid="player-livup-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/portal" dark />
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white hover:bg-white/5" data-testid="livup-logout-button">
            <LogOut className="h-4 w-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 pt-6 space-y-6">
        <Link to="/portal" className="inline-flex items-center text-sm text-white/50 hover:text-white">
          <ChevronLeft className="h-4 w-4" /> Back to today&apos;s log
        </Link>

        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Sync · LIVUP smart ring</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mt-1">LIVUP ring</h1>
          <p className="text-sm text-white/50 mt-1">
            LIVUP doesn&apos;t bridge into Apple Health, so log the four numbers off your LIVUP &quot;Daily Report&quot;
            tab and we&apos;ll fold them into your Readiness Score alongside any Apple Watch data.
          </p>
        </div>

        {/* Daily entry — fastest path */}
        <form onSubmit={submitEntry} className="rounded-xl ink-card p-5" data-testid="livup-entry-form">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Log today&apos;s ring data</div>
              <div className="text-sm text-white/60 mt-1">Snap your LIVUP &quot;Daily Report&quot; page or type the numbers in — both work.</div>
            </div>
            {data?.last_sync && (
              <div className="text-[10px] tracking-[0.2em] text-white/40 font-mono-num">
                Last sync: {new Date(data.last_sync).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
            )}
          </div>

          <div className="mt-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              capture="environment"
              className="hidden"
              onChange={handleScan}
              data-testid="livup-scan-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={scanBusy}
              className="border-white/15 text-white bg-white/5 hover:bg-white/10"
              data-testid="livup-scan-btn"
            >
              {scanBusy ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Reading screenshot…</>
              ) : (
                <><Camera className="h-4 w-4 mr-1.5" /> Scan LIVUP screenshot</>
              )}
            </Button>
            <span className="ml-3 text-xs text-white/40">or type the numbers in below</span>
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {ENTRY_FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`livup-${f.key}`} className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">
                  {f.label}{f.unit ? ` · ${f.unit}` : ""}
                </Label>
                <Input
                  id={`livup-${f.key}`}
                  data-testid={`livup-input-${f.key}`}
                  type="number"
                  step={f.step}
                  placeholder={f.placeholder}
                  value={entry[f.key] ?? ""}
                  onChange={(e) => setEntry((p) => ({ ...p, [f.key]: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white text-sm h-9"
                />
              </div>
            ))}
          </div>

          <div className="mt-5 flex items-center justify-end gap-3">
            <Button type="submit" disabled={busy} data-testid="livup-entry-submit">
              <Upload className="h-4 w-4 mr-1.5" /> {busy ? "Saving…" : "Log to ArmIQ"}
            </Button>
          </div>
        </form>

        {/* Latest metrics */}
        {sorted.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="livup-latest-metrics">
            <MetricCard Icon={Activity}   label="HRV"        value={latest.hrv_avg?.toFixed(0)}        unit="ms"  color="#22D3EE" />
            <MetricCard Icon={Heart}      label="Resting HR" value={latest.resting_hr?.toFixed(0)}     unit="bpm" color="#EF4444" />
            <MetricCard Icon={Moon}       label="Sleep"      value={latest.sleep_hours?.toFixed(1)}    unit="hrs" color="#A78BFA" />
            <MetricCard Icon={Moon}       label="Deep"       value={latest.sleep_deep_hours?.toFixed(1)} unit="hrs" color="#818CF8" />
            <MetricCard Icon={Zap}        label="SpO₂"       value={latest.spo2_pct?.toFixed(0)}       unit="%"   color="#34D399" />
            <MetricCard Icon={Footprints} label="Steps"      value={latest.steps?.toLocaleString()}    color="#34D399" />
          </div>
        )}

        {/* 14-day mini-chart */}
        {chart.length > 1 && (
          <div className="rounded-xl ink-card p-5" data-testid="livup-chart">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">HRV — last 14 days</div>
            <div className="h-44 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chart}>
                  <defs>
                    <linearGradient id="livupHrvGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#22D3EE" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F3354" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #2C4570", backgroundColor: "#0F1E36", color: "#fff" }} />
                  <Area type="monotone" dataKey="hrv" stroke="#22D3EE" strokeWidth={2} fill="url(#livupHrvGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Webhook URL — power users wire up an iOS Shortcut */}
        <div className="rounded-xl ink-card p-5" data-testid="livup-webhook-block">
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Automate it · webhook URL</div>
          <div className="text-sm text-white/60 mt-1">
            Build an iOS Shortcut or Tasker action that POSTs your LIVUP daily data to this URL each morning.
            Send JSON like <code className="font-mono-num text-xs px-1 py-0.5 rounded bg-white/5 text-white/80">{`{"hrv_ms": 68, "rhr_bpm": 54, "sleep_minutes": 456}`}</code>.
          </div>
          {data?.webhook_url && (
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 px-3 py-2 rounded bg-black/30 text-xs text-white/70 font-mono-num break-all" data-testid="livup-webhook-url">
                {data.webhook_url}
              </code>
              <Button variant="outline" size="sm" onClick={copy} className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="livup-copy-btn">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
              <Button variant="outline" size="sm" onClick={rotate} className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="livup-rotate-btn">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>

        {/* Apple Watch deep link */}
        <div className="text-xs text-white/40 text-center">
          Also wear an Apple Watch or Wear OS device? <Link to="/portal/apple-health" className="underline" style={{ color: "var(--brand-secondary)" }}>Connect that here →</Link>
        </div>
      </div>
    </div>
  );
}
